import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { Gauge, Syringe, ScanEye, TrendingUp, Scissors, Loader2 } from 'lucide-react';
import { COLOR_OJO, dec, fechaCorta } from '../../lib/oftalmo';

/**
 * Panel de inicio de oftalmología (4/11/2026): lo que hay que mirar hoy en
 * toda la clínica (o en los pacientes de cada médico). Cada nombre lleva a su
 * historia oftalmológica.
 */
function Tarjeta({ icono: Icono, titulo, n, vacio, children, color, testId }) {
  return (
    <div className="cp-card p-4 flex flex-col" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 font-display font-semibold text-sm">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}1A` }}>
            <Icono className="w-4 h-4" style={{ color }} />
          </span>
          {titulo}
        </div>
        <span className="text-2xl font-display font-semibold tabular-nums" style={{ color: n ? color : undefined }}>{n}</span>
      </div>
      <div className="text-sm space-y-1 max-h-48 overflow-y-auto">
        {n ? children : <div className="text-xs text-muted-foreground">{vacio}</div>}
      </div>
    </div>
  );
}

const Fila = ({ x, children, abajo = false }) => (
  <Link to={`/pacientes/${x.patient_id}`}
    className={`${abajo ? 'block' : 'flex items-center justify-between gap-2'} rounded-md px-1.5 py-1 hover:bg-[var(--cp-soft)] dark:hover:bg-muted/40`}>
    <span className={`${abajo ? 'block' : ''} truncate min-w-0`}>{x.paciente}</span>
    <span className={`${abajo ? 'block text-muted-foreground truncate' : 'shrink-0'} text-xs`}>{children}</span>
  </Link>
);
const Ojo = ({ o }) => <b style={{ color: COLOR_OJO[o] }}>{o}</b>;

export default function PanelOftalmologia() {
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    api.get('/api/oftalmologia/panel').then((r) => setD(r.data)).catch(() => setError(true));
  }, []);
  if (error) return null;
  if (!d) return <div className="cp-card p-6 mb-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#2E6E5E]" /></div>;
  const r = d.recuentos;
  return (
    <div className="mb-6" data-testid="panel-oftalmologia">
      <div className="font-display font-semibold mb-2">Oftalmología · para mirar hoy</div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <Tarjeta icono={Syringe} titulo="Inyecciones que tocan" n={r.inyecciones} color="#15803D" vacio="Ninguna esta semana." testId="panel-inyecciones">
          {d.inyecciones.map((x) => (
            <Fila key={`${x.patient_id}${x.ojo}`} x={x}>
              <Ojo o={x.ojo} /> · nº {x.numero + 1} · <span className={x.atrasada ? 'text-red-600 font-semibold' : ''}>{x.atrasada ? 'atrasada ' : ''}{fechaCorta(x.proxima)}</span>
            </Fila>
          ))}
        </Tarjeta>
        <Tarjeta icono={Gauge} titulo="PIO por encima del objetivo" n={r.pio_alta} color="#DC2626" vacio="Todas en su objetivo." testId="panel-pio">
          {d.pio_alta.map((x) => (
            <Fila key={`${x.patient_id}${x.ojo}`} x={x}>
              <Ojo o={x.ojo} /> <b>{dec(x.valor, 0)}</b> mmHg{x.objetivo != null ? ` (obj. ${dec(x.objetivo, 0)})` : ''} · {fechaCorta(x.fecha)}
            </Fila>
          ))}
        </Tarjeta>
        <Tarjeta icono={ScanEye} titulo="Grosor macular que sube" n={r.cst_sube} color="#2563EB" vacio="Sin subidas en la última OCT." testId="panel-cst">
          {d.cst_sube.map((x) => (
            <Fila key={`${x.patient_id}${x.ojo}`} x={x}><Ojo o={x.ojo} /> {dec(x.antes, 0)} → <b>{dec(x.ahora, 0)}</b> µm</Fila>
          ))}
        </Tarjeta>
        <Tarjeta icono={TrendingUp} titulo="Kmax que progresa" n={r.kmax_sube} color="#9333EA" vacio="Sin progresión en las topografías." testId="panel-kmax">
          {d.kmax_sube.map((x) => (
            <Fila key={`${x.patient_id}${x.ojo}`} x={x}><Ojo o={x.ojo} /> {dec(x.antes)} → <b>{dec(x.ahora)}</b> D</Fila>
          ))}
        </Tarjeta>
        <Tarjeta icono={Scissors} titulo="Cirugías del último mes" n={r.cirugias_mes} color="#B45309" vacio="Ninguna en los últimos 30 días." testId="panel-cirugias">
          {d.cirugias_mes.map((x, i) => <Fila key={i} x={x} abajo>{x.que} · {fechaCorta(x.fecha)}</Fila>)}
        </Tarjeta>
        <Link to="/datos-estudio" className="cp-card p-4 flex flex-col justify-center hover:border-[#2E6E5E] text-sm">
          <div className="font-display font-semibold">Datos para estudio →</div>
          <div className="text-xs text-muted-foreground mt-1">Resultados de LIO y refractiva, PIO, OCT… exportables a Excel.</div>
        </Link>
      </div>
    </div>
  );
}
